<script setup>
import { RouterLink } from 'vue-router';
import { longDate } from '../../helpers/usDate';
import XlButton from '../xl/XlButton.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlTag from '../xl/XlTag.vue';
import IconPlay from '../icons/IconPlay.vue';
import IconSparkle from '../icons/IconSparkle.vue';

/**
 * The Recordings tab of Sessions (XN-4558): the prototype's `.reccard` rows
 * (build 30 Sep 12:40). Watch progress and 'You were there' are left out;
 * neither is tracked.
 */
defineProps({
  recordings: { type: Array, default: () => [] },
  state: { type: String, default: 'ready' },
  agency: { type: String, required: true },
});
const emit = defineEmits(['retry']);

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const formatDate = (iso) => longDate(iso);

// XN-4688: has_video false = recap only, so no video details; absent (older payload) = video.
// Joined rather than punctuated in the template: a row with no host, or one the
// agent never anchored to a start time, would strand a separator.
const metaFor = (item) => [
  item.host,
  formatDate(item.recorded_at),
  item.part_count > 1 && item.has_video !== false ? `${item.part_count} parts` : '',
].filter(Boolean).join(' · ');
</script>

<template>
  <div class="rec">
    <div v-if="state === 'loading'" class="rec__loading">
      <XlSkeleton v-for="n in 3" :key="n" height="104px" />
    </div>

    <p v-else-if="state === 'error'" class="rec__error" role="alert">
      <span>Your recordings couldn't be loaded.</span>
      <XlButton variant="ghost" size="sm" data-testid="recordings-retry" @click="emit('retry')">Try again</XlButton>
    </p>

    <XlEmptyState
      v-else-if="!recordings.length"
      class="rec__empty"
      headline="Nothing to watch yet"
      :body="`Recordings appear here once ${agency} has checked them.`"
    />

    <template v-else>
      <ul class="rec__list">
        <li v-for="item in recordings" :key="item.id">
          <RouterLink class="rec__item" :to="`/hub/recordings/${item.id}`">
            <span class="rec__thumb" :class="{ 'rec__thumb--recap': item.has_video === false }">
              <span class="rec__thumb-icon" aria-hidden="true">
                <IconSparkle v-if="item.has_video === false" :size="34" />
                <IconPlay v-else color="currentColor" size="26" />
              </span>
              <span v-if="item.duration_ms && item.has_video !== false" class="rec__badge">{{ formatDuration(item.duration_ms) }}</span>
            </span>
            <span class="rec__text">
              <b>{{ item.title || 'Session recording' }}</b>
              <span v-if="item.description" class="rec__desc">{{ item.description }}</span>
              <span class="rec__meta">{{ metaFor(item) }}<XlTag v-if="item.has_video === false" tone="brand">Recap</XlTag></span>
            </span>
            <span class="rec__watch">{{ item.has_video === false ? 'Read the recap' : 'Watch' }}</span>
          </RouterLink>
        </li>
      </ul>

      <p class="rec__note">Recordings appear here once {{ agency }} has checked them.</p>
    </template>
  </div>
</template>

<style scoped>
.rec__loading { display: grid; gap: 12px; }

.rec__empty {
  border-radius: 24px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  box-shadow: 0 1px 2px rgba(28, 23, 32, .04);
}

.rec__error {
  margin: 0;
  padding: 18px 20px;
  border-radius: 16px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  font-size: 14.5px;
  color: var(--xl-color-secondary-text);
}

.rec__error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  background: var(--xl-color-error-container, #FBEAEA);
  border-color: var(--xl-color-error);
  color: var(--xl-color-error);
}

.rec__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }

.rec__item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px;
  border: 1px solid var(--xl-color-border);
  border-radius: 18px;
  background: #fff;
  box-shadow: var(--xl-shadow-1);
  text-decoration: none;
  color: inherit;
  transition: border-color .13s, box-shadow .13s;
}

.rec__item:hover { border-color: var(--xl-color-primary-fixed-dim); box-shadow: var(--xl-shadow-2); }
.rec__item:focus-visible { outline: 2px solid var(--xl-color-surface-tint); outline-offset: 2px; }

.rec__thumb {
  position: relative;
  display: grid;
  place-items: center;
  width: 148px;
  height: 84px;
  flex: 0 0 148px;
  border-radius: 12px;
  background: linear-gradient(150deg, var(--xl-color-primary-container), var(--xl-color-primary-fixed-dim));
  color: var(--xl-color-surface-tint);
}

/* XN-4688 (Greg's frame 1908:8765): a recap has no picture to show, so a flat tile and a spark. */
.rec__thumb--recap { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }

.rec__thumb-icon { display: grid; place-items: center; }

.rec__badge {
  position: absolute;
  right: 7px;
  bottom: 7px;
  padding: 2px 7px;
  border-radius: 7px;
  background: rgba(28, 23, 32, .78);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
}

.rec__text { flex: 1; min-width: 0; }
.rec__text b { display: block; font-size: 16px; color: var(--xl-color-primary-text); }

.rec__desc {
  display: block;
  margin-top: 3px;
  font-size: 14px;
  color: var(--xl-color-secondary-text);
}

.rec__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 6px; font-size: 12.5px; color: var(--xl-color-secondary-text); }

/* A span, not a button: it sits inside the card's link, and interactive
   content nested in a link is invalid and reads twice to a screen reader. */
.rec__watch {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: 34px;
  padding: 0 15px;
  border: 1px solid var(--xl-color-outline);
  border-radius: 999px;
  background: var(--xl-color-surface);
  color: var(--xl-color-primary);
  font-size: 13px;
  font-weight: 600;
}

.rec__item:hover .rec__watch { background: var(--xl-color-primary-container); }

.rec__note { margin: 18px 0 0; font-size: 12.5px; color: var(--xl-color-secondary-text); text-align: center; }

@media (max-width: 600px) {
  .rec__item { flex-wrap: wrap; }
  .rec__thumb { width: 100%; flex-basis: 100%; height: 150px; }
  .rec__watch { width: 100%; }
}
</style>
